/**
 * <bundle-save-widget>
 *
 * Custom element wrapping the Bundle & Save modal contents.
 *
 * - Lazy-loads each campaign section via the Section Rendering API
 *   on tab activation, caches by fetch URL (variant-aware).
 * - Selection-first flow: "+ Add" toggles card selection, reveals
 *   bundle-ready summary, "Add Bundle to Cart" sends both items
 *   to /cart/add.js with _bundle_campaign line item property.
 */
if (!customElements.get("bundle-save-widget")) {
    /**
     * Campaign sections whose rendered content depends on the main rug's
     * selected variant (shape / length_in / width_in metafields). Only
     * these panels get `?variant=<id>` in their fetch URL and only these
     * are invalidated + refetched when the drawer changes the main variant.
     *
     * The others (runner, decor, jute, mat, cleaning) are collection-based
     * listings whose content is stable regardless of size/shape, so
     * refetching them on every size-chip click is wasted work.
     */
    const VARIANT_SENSITIVE_SECTIONS = ["bundle-campaign-pad"];

    class BundleSaveWidget extends HTMLElement {
        constructor() {
            super();
            this._cache = new Map();
            this._skeletons = null;
            this._tabsBound = false;
            this._clicksBound = false;
            this._modalObserver = null;
            this._handleModalMutation = this._handleModalMutation.bind(this);
            this._handleBundleClick = this._handleBundleClick.bind(this);
            this._handleDrawerOpen = this._handleDrawerOpen.bind(this);
        }

        connectedCallback() {
            // The widget can be hosted by either <modal-dialog> or
            // <bundle-save-drawer> (extends SideDrawer). Resolve whichever
            // ancestor is present and treat it uniformly via _host.
            this._modal = this.closest("modal-dialog");
            this._drawer = this.closest("bundle-save-drawer");
            this._host = this._modal || this._drawer || null;
            this._tabList = this.querySelector('[role="tablist"]');

            if (!this._tabList) return;

            // Capture skeleton markup once for restore during re-fetches.
            if (!this._skeletons) {
                this._skeletons = new Map();
                const panels = this.querySelectorAll('[role="tabpanel"]');
                for (const panel of panels) {
                    if (panel.id)
                        this._skeletons.set(panel.id, panel.innerHTML);
                }
            }

            // tabs.js dispatches 'tab:activated' from activateTab() —
            // covers click, arrow keys, Home, End.
            if (!this._tabsBound) {
                this._tabsBound = true;
                this._tabList.addEventListener(
                    "tab:activated",
                    this._handleTabActivated.bind(this),
                );
            }

            // Delegate clicks + select changes at the widget root
            // so dynamically loaded content is automatically covered.
            if (!this._clicksBound) {
                this._clicksBound = true;
                this.addEventListener("click", this._handleBundleClick);
                this.addEventListener(
                    "change",
                    this._handleVariantChange.bind(this),
                );
            }

            if (this._isHostOpen()) {
                this._loadActiveTabPanel();
            }

            // Modal host: watch [open] attribute to lazy-load on first open.
            if (this._modal && !this._modalObserver) {
                this._modalObserver = new MutationObserver(
                    this._handleModalMutation,
                );
                this._modalObserver.observe(this._modal, {
                    attributes: true,
                    attributeFilter: ["open"],
                });
            }

            // Drawer host: SideDrawer dispatches on:bundle-drawer:after-open.
            if (this._drawer) {
                this._drawer.addEventListener(
                    "on:bundle-drawer:after-open",
                    this._handleDrawerOpen,
                );
            }
        }

        disconnectedCallback() {
            if (this._modalObserver) {
                this._modalObserver.disconnect();
                this._modalObserver = null;
            }
            if (this._drawer) {
                this._drawer.removeEventListener(
                    "on:bundle-drawer:after-open",
                    this._handleDrawerOpen,
                );
            }
        }

        _isHostOpen() {
            if (!this._host) return false;
            return this._host.hasAttribute("open");
        }

        _closeHost() {
            if (this._modal && typeof this._modal.close === "function")
                this._modal.close();
            else if (this._drawer && typeof this._drawer.close === "function")
                this._drawer.close();
        }

        _handleDrawerOpen() {
            this._loadActiveTabPanel();
        }

        /**
         * Public: called by <bundle-save-drawer> when the user picks a new
         * shape/size in the drawer. Updates the widget's main variant id +
         * recomputes any rendered summaries (discount, total, savings) so the
         * cart-add will use the new variant and the user sees a live preview.
         */
        setMainVariant(variantId, mainPriceCents, variantLabel) {
            const idStr = String(variantId);
            const price = parseInt(mainPriceCents, 10) || 0;
            const previousId = this.dataset.mainVariantId;
            this.dataset.mainVariantId = idStr;
            this._mainPriceCents = price;
            this._mainVariantLabel = variantLabel || "";

            // Only the variant-sensitive panels (rug-pad) need to re-render
            // when the main variant changes. Collection-based panels stay as
            // they are — their HTML doesn't depend on the selected variant.
            // Invalidate the sensitive ones so they refetch on next view, and
            // refetch the active panel immediately only if it is sensitive.
            if (previousId !== idStr) {
                for (const panel of this.querySelectorAll(
                    '[role="tabpanel"]',
                )) {
                    if (
                        !VARIANT_SENSITIVE_SECTIONS.includes(
                            panel.dataset.sectionId,
                        )
                    )
                        continue;
                    delete panel.dataset.loadedUrl;
                    delete panel.dataset.loaded;
                }
                if (this._isHostOpen()) {
                    const activeTab = this._tabList.querySelector(
                        '[role="tab"][aria-selected="true"]',
                    );
                    const activePanelId =
                        activeTab && activeTab.getAttribute("aria-controls");
                    const activePanel =
                        activePanelId && document.getElementById(activePanelId);
                    if (
                        activePanel &&
                        VARIANT_SENSITIVE_SECTIONS.includes(
                            activePanel.dataset.sectionId,
                        )
                    ) {
                        this._loadCampaignIntoPanel(activePanel);
                    }
                }
            }

            for (const summary of this.querySelectorAll(
                "[data-bundle-summary]",
            )) {
                const pct = parseFloat(summary.dataset.discountPct) || 0;
                const discountedMain = Math.round(price * (1 - pct / 100));
                const savings = price - discountedMain;

                summary.dataset.discountedMainPrice = String(discountedMain);

                const oldEl = summary.querySelector(
                    "[data-bundle-main-old-price]",
                );
                const newEl = summary.querySelector(
                    "[data-bundle-main-new-price]",
                );
                const savingsEl = summary.querySelector(
                    "[data-bundle-savings-amount]",
                );
                const labelEl = summary.querySelector(
                    "[data-bundle-main-row-label]",
                );
                if (oldEl) oldEl.textContent = this._formatMoney(price);
                if (newEl)
                    newEl.textContent = this._formatMoney(discountedMain);
                if (savingsEl)
                    savingsEl.textContent = this._formatMoney(savings);

                if (labelEl) {
                    const productTitle = summary.dataset.mainProductTitle || "";
                    labelEl.textContent = this._mainVariantLabel
                        ? `${productTitle} (${this._mainVariantLabel})`
                        : productTitle;
                }

                const confirmBtn = summary.querySelector(
                    "[data-bundle-confirm]",
                );
                if (confirmBtn) confirmBtn.dataset.mainVariantId = idStr;

                // If a companion is currently selected, recompute the total row
                // by re-running the existing card-select handler against the
                // currently-selected card in this campaign.
                if (!summary.hidden) {
                    const campaign = summary.closest(".bundle-campaign");
                    const selectedBtn =
                        campaign &&
                        campaign.querySelector(
                            "[data-bundle-card].is-selected [data-bundle-add]",
                        );
                    if (selectedBtn) {
                        // _handleCardSelect toggles off-then-on; bypass the toggle by
                        // recomputing the total inline:
                        const variantPrice =
                            parseInt(selectedBtn.dataset.variantPrice, 10) || 0;
                        const totalEl = summary.querySelector(
                            "[data-bundle-total]",
                        );
                        if (totalEl)
                            totalEl.textContent = this._formatMoney(
                                discountedMain + variantPrice,
                            );
                    }
                    // Notify host so the drawer footer updates its savings text.
                    this._dispatchSelectionChange(summary);
                }
            }
        }

        /* ── Tab activation ── */

        _handleModalMutation(mutations) {
            for (const m of mutations) {
                if (
                    m.attributeName === "open" &&
                    this._modal &&
                    this._modal.hasAttribute("open")
                ) {
                    this._loadActiveTabPanel();
                    return;
                }
            }
        }

        _handleTabActivated(event) {
            const tab = event.target;
            const panelId = tab.getAttribute("aria-controls");
            if (!panelId) return;

            // A bundle only contains one companion item; switching tabs means
            // the user is reconsidering which type to add. Clear all selection
            // state (card highlight, summary, variant select) across every tab
            // so the new tab starts from a clean slate and nothing stale lurks
            // behind the scenes.
            this._resetAllPanelState();

            const panel = document.getElementById(panelId);
            if (panel) this._loadCampaignIntoPanel(panel);
        }

        _resetAllPanelState() {
            // Card + summary reset first — removes is-selected so the ensuing
            // select change events don't re-trigger _handleCardSelect.
            this._resetSelections();

            // Reset any variant selects inside cards to their first option and
            // fire a change event so the card's add-btn data-attributes (and
            // displayed price) snap back to the default variant.
            for (const select of this.querySelectorAll(
                "[data-bundle-variant-select]",
            )) {
                if (select.options.length === 0) continue;
                if (select.selectedIndex === 0) continue;
                select.selectedIndex = 0;
                select.dispatchEvent(new Event("change", { bubbles: true }));
            }
        }

        _loadActiveTabPanel() {
            const activeTab = this._tabList.querySelector(
                '[role="tab"][aria-selected="true"]',
            );
            if (!activeTab) return;
            const panelId = activeTab.getAttribute("aria-controls");
            const panel = document.getElementById(panelId);
            if (panel) this._loadCampaignIntoPanel(panel);
        }

        /* ── Section Rendering API fetch ── */

        async _loadCampaignIntoPanel(panel) {
            const sectionId = panel.dataset.sectionId;
            if (!sectionId) return;

            const url = new URL(window.location.href);
            url.searchParams.set("section_id", sectionId);
            // Only variant-sensitive sections get the variant param — their
            // Liquid reads selected_variant metafields (pad-size matching).
            // Variant-agnostic panels stay cacheable and don't need to refetch
            // when the drawer changes the rug size.
            const mainVariantId = this.dataset.mainVariantId;
            if (
                mainVariantId &&
                VARIANT_SENSITIVE_SECTIONS.includes(sectionId)
            ) {
                url.searchParams.set("variant", mainVariantId);
            }
            const fetchUrl = url.pathname + url.search;

            // Already loaded with this exact URL — skip.
            if (panel.dataset.loadedUrl === fetchUrl) return;
            panel.dataset.loadedUrl = fetchUrl;

            // Signal loading state to assistive tech.
            panel.setAttribute("aria-busy", "true");

            // Restore skeleton while loading.
            const skeleton = this._skeletons?.get(panel.id);
            if (skeleton) panel.innerHTML = skeleton;

            // Cache hit — inject immediately.
            const cached = this._cache.get(fetchUrl);
            if (cached) {
                this._injectIntoPanel(panel, cached);
                return;
            }

            try {
                const response = await fetch(fetchUrl, {
                    credentials: "same-origin",
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const html = await response.text();

                this._cache.set(fetchUrl, html);
                if (panel.dataset.loadedUrl === fetchUrl) {
                    this._injectIntoPanel(panel, html);
                }
            } catch (err) {
                console.error("[bundle-save-widget]", err);
                if (panel.dataset.loadedUrl === fetchUrl) {
                    delete panel.dataset.loadedUrl;
                }
                panel.removeAttribute("aria-busy");
                panel.innerHTML =
                    '<div class="bundle-panel__error" role="alert">Failed to load this bundle. Please refresh and try again.</div>';
            }
        }

        _injectIntoPanel(panel, html) {
            panel.innerHTML = html;
            panel.dataset.loaded = "true";
            panel.removeAttribute("aria-busy");

            this._refreshTrapFocus();
            this._focusFirstInteractive(panel);

            // Auto-load first active sub-tab if present (e.g. decor).
            const firstSubTab = panel.querySelector(
                "[data-sub-tab].is-active[data-search-query]",
            );
            if (firstSubTab) {
                const key = firstSubTab.dataset.subTab;
                const subPanel = panel.querySelector(
                    `[data-sub-panel="${key}"]`,
                );
                if (subPanel && subPanel.dataset.loaded !== "true") {
                    this._loadSubPanel(
                        subPanel,
                        firstSubTab.dataset.searchQuery,
                    );
                }
            }

            // Auto-load single search panel (e.g. cleaning).
            const autoPanel = panel.querySelector("[data-auto-search]");
            if (autoPanel && autoPanel.dataset.loaded !== "true") {
                this._loadSubPanel(autoPanel, autoPanel.dataset.autoSearch);
            }
        }

        /* ── Click delegation ── */

        _handleBundleClick(event) {
            const subTab = event.target.closest("[data-sub-tab]");
            if (subTab) {
                this._handleSubTabClick(subTab);
                return;
            }

            const addBtn = event.target.closest("[data-bundle-add]");
            if (addBtn) {
                event.preventDefault();
                this._handleCardSelect(addBtn);
                return;
            }

            const confirmBtn = event.target.closest("[data-bundle-confirm]");
            if (confirmBtn) {
                event.preventDefault();
                this._handleConfirmAdd(confirmBtn);
                return;
            }

            // Whole-card click: if the click landed on a bundle card but not
            // on an interactive element we already handle above (links to the
            // product page, the variant <select>), treat it as a "+ add"
            // click. Keeps the tap target as large as the card.
            const card = event.target.closest("[data-bundle-card]");
            if (!card) return;
            if (event.target.closest("a, [data-bundle-variant-select]")) return;
            const cardAdd = card.querySelector("[data-bundle-add]");
            if (cardAdd) {
                event.preventDefault();
                this._handleCardSelect(cardAdd);
            }
        }

        /* ── Sub-tabs (decor type filters) ── */

        _handleSubTabClick(subTab) {
            const container = subTab.closest("[data-sub-tabs]");
            if (!container) return;
            const campaign = container.closest(".bundle-campaign");
            if (!campaign) return;

            const key = subTab.dataset.subTab;

            for (const tab of container.querySelectorAll("[data-sub-tab]")) {
                tab.classList.remove("is-active");
                tab.setAttribute("aria-selected", "false");
            }
            subTab.classList.add("is-active");
            subTab.setAttribute("aria-selected", "true");

            for (const panel of campaign.querySelectorAll("[data-sub-panel]")) {
                const isActive = panel.dataset.subPanel === key;
                panel.hidden = !isActive;
                panel.setAttribute("aria-hidden", String(!isActive));
            }

            const activePanel = campaign.querySelector(
                `[data-sub-panel="${key}"]`,
            );
            const searchQuery = subTab.dataset.searchQuery;
            if (
                activePanel &&
                searchQuery &&
                activePanel.dataset.loaded !== "true"
            ) {
                this._loadSubPanel(activePanel, searchQuery);
            }
        }

        async _loadSubPanel(panel, searchQuery) {
            const fetchUrl =
                "/search?type=product&options%5Bunavailable_products%5D=hide&q=" +
                encodeURIComponent(searchQuery) +
                "&section_id=bundle-search-cards";

            const campaign = panel.closest("[data-campaign-code]");
            const campaignCode = campaign?.dataset.campaignCode || "";

            try {
                const response = await fetch(fetchUrl, {
                    credentials: "same-origin",
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const html = await response.text();

                panel.innerHTML = html;
                panel.dataset.loaded = "true";

                if (campaignCode) {
                    for (const btn of panel.querySelectorAll(
                        "[data-bundle-add]",
                    )) {
                        btn.dataset.campaignCode = campaignCode;
                    }
                }

                this._refreshTrapFocus();
                this._focusFirstInteractive(panel);
            } catch (err) {
                console.error(
                    "[bundle-save-widget] sub-panel fetch failed:",
                    err,
                );
                panel.innerHTML =
                    '<div class="bundle-panel__error">Failed to load products.</div>';
            }
        }

        /* ── Variant select change ── */

        _handleVariantChange(event) {
            const select = event.target.closest("[data-bundle-variant-select]");
            if (!select) return;
            const card = select.closest("[data-bundle-card]");
            if (!card) return;

            const option = select.options[select.selectedIndex];
            const variantId = option.value;
            const variantPrice = parseInt(option.dataset.price, 10) || 0;
            const variantTitle = option.dataset.title || "";

            const addBtn = card.querySelector("[data-bundle-add]");
            if (addBtn) {
                addBtn.dataset.variantId = variantId;
                addBtn.dataset.variantPrice = String(variantPrice);
                addBtn.dataset.variantSubtitle = variantTitle;
            }

            const priceEl = card.querySelector(".bundle-product-card__price");
            if (priceEl) priceEl.textContent = this._formatMoney(variantPrice);

            if (card.classList.contains("is-selected") && addBtn) {
                this._handleCardSelect(addBtn);
            }
        }

        /* ── Card selection + summary ── */

        _handleCardSelect(addBtn) {
            const card = addBtn.closest("[data-bundle-card]");
            const campaign = addBtn.closest(".bundle-campaign");
            if (!card || !campaign) return;
            const summary = campaign.querySelector("[data-bundle-summary]");
            if (!summary) return;

            const wasSelected = card.classList.contains("is-selected");

            for (const c of campaign.querySelectorAll(
                "[data-bundle-card].is-selected",
            )) {
                c.classList.remove("is-selected");
            }

            if (wasSelected) {
                summary.hidden = true;
                this._dispatchSelectionChange(null);
                return;
            }

            card.classList.add("is-selected");

            const variantId = addBtn.dataset.variantId;
            const variantPrice = parseInt(addBtn.dataset.variantPrice, 10) || 0;
            const variantName = addBtn.dataset.variantName || "";
            const variantSubtitle = addBtn.dataset.variantSubtitle || "";
            const displayName = variantSubtitle
                ? `${variantName} (${variantSubtitle})`
                : variantName;

            const nameEl = summary.querySelector("[data-bundle-item-name]");
            const priceEl = summary.querySelector("[data-bundle-item-price]");
            if (nameEl) nameEl.textContent = displayName;
            if (priceEl) priceEl.textContent = this._formatMoney(variantPrice);

            const discountedMain =
                parseInt(summary.dataset.discountedMainPrice, 10) || 0;
            const total = discountedMain + variantPrice;
            const totalEl = summary.querySelector("[data-bundle-total]");
            if (totalEl) totalEl.textContent = this._formatMoney(total);

            const confirmBtn = summary.querySelector("[data-bundle-confirm]");
            if (confirmBtn) confirmBtn.dataset.bundleVariantId = variantId;

            this._clearError(summary);
            summary.hidden = false;
            summary.scrollIntoView?.({ behavior: "smooth", block: "nearest" });

            this._dispatchSelectionChange(summary);
        }

        /**
         * Notify host (drawer footer, etc.) of selection state changes.
         * Pass `null` for deselection. Reads everything off the summary so
         * variant changes via setMainVariant can re-fire this with up-to-date
         * totals/savings.
         */
        _dispatchSelectionChange(summary) {
            if (!summary) {
                this.dispatchEvent(
                    new CustomEvent("bundle:selection-change", {
                        detail: { selected: false },
                        bubbles: true,
                    }),
                );
                return;
            }
            const confirmBtn = summary.querySelector("[data-bundle-confirm]");
            const mainPrice =
                this._mainPriceCents != null
                    ? this._mainPriceCents
                    : parseInt(this.dataset.mainPriceCents, 10) || 0;
            const discountedMain =
                parseInt(summary.dataset.discountedMainPrice, 10) || 0;
            const savingsCents = mainPrice
                ? Math.max(0, mainPrice - discountedMain)
                : 0;
            const itemPriceEl = summary.querySelector(
                "[data-bundle-item-price]",
            );
            const itemPrice = itemPriceEl
                ? this._parseMoneyText(itemPriceEl.textContent)
                : 0;
            const total = discountedMain + (itemPrice || 0);

            this.dispatchEvent(
                new CustomEvent("bundle:selection-change", {
                    detail: {
                        selected: true,
                        mainVariantId:
                            confirmBtn?.dataset.mainVariantId ||
                            this.dataset.mainVariantId,
                        bundleVariantId:
                            confirmBtn?.dataset.bundleVariantId || "",
                        campaignCode: confirmBtn?.dataset.campaignCode || "",
                        savingsCents,
                        savingsFormatted: this._formatMoney(savingsCents),
                        totalCents: total,
                        totalFormatted: this._formatMoney(total),
                    },
                    bubbles: true,
                }),
            );
        }

        _parseMoneyText(text) {
            if (!text) return 0;
            const digits = String(text).replace(/[^0-9.]/g, "");
            if (!digits) return 0;
            return Math.round(parseFloat(digits) * 100);
        }

        /* ── Add bundle to cart ── */

        async _handleConfirmAdd(confirmBtn) {
            const mainVariantId = parseInt(
                confirmBtn.dataset.mainVariantId,
                10,
            );
            const bundleVariantId = parseInt(
                confirmBtn.dataset.bundleVariantId,
                10,
            );
            const campaignCode = confirmBtn.dataset.campaignCode;
            if (!mainVariantId || !bundleVariantId || !campaignCode) return;

            const summary = confirmBtn.closest("[data-bundle-summary]");
            this._clearError(summary);

            confirmBtn.setAttribute("aria-live", "polite");
            const originalLabel = confirmBtn.textContent;
            confirmBtn.disabled = true;
            confirmBtn.textContent = "Adding...";

            const cartDrawerEl = document.querySelector("cart-drawer");
            let sectionsParam = "cart-icon-bubble";
            if (cartDrawerEl) {
                const wrapper = cartDrawerEl.closest(".shopify-section");
                if (wrapper?.id) {
                    sectionsParam +=
                        "," + wrapper.id.replace("shopify-section-", "");
                }
            }

            const cartAddUrl = window.theme?.routes?.cartAdd || "/cart/add.js";

            try {
                const response = await fetch(cartAddUrl, {
                    method: "POST",
                    headers: {
                        Accept: "application/javascript",
                        "Content-Type": "application/json",
                        "X-Requested-With": "XMLHttpRequest",
                    },
                    body: JSON.stringify({
                        items: [
                            {
                                id: mainVariantId,
                                quantity: 1,
                                properties: { _bundle_campaign: campaignCode },
                            },
                            {
                                id: bundleVariantId,
                                quantity: 1,
                                properties: { _bundle_campaign: campaignCode },
                            },
                        ],
                        sections: sectionsParam,
                        sections_url: window.location.pathname,
                    }),
                });

                const data = await response.json();
                if (!response.ok || data.status) {
                    throw new Error(
                        data.description ||
                            data.message ||
                            `HTTP ${response.status}`,
                    );
                }

                if (cartDrawerEl?.renderContents) {
                    cartDrawerEl.renderContents(data, true);
                }

                this.dispatchEvent(
                    new CustomEvent("bundle:add-success", {
                        detail: {
                            mainVariantId,
                            bundleVariantId,
                            campaignCode,
                        },
                        bubbles: true,
                    }),
                );

                this._closeHost();
                this._resetSelections();
            } catch (err) {
                console.error("[bundle-save-widget]", err);
                confirmBtn.disabled = false;
                confirmBtn.textContent = originalLabel;
                this._showError(
                    summary,
                    err.message || "Something went wrong.",
                );
                this.dispatchEvent(
                    new CustomEvent("bundle:add-error", {
                        detail: {
                            message: err.message || "Something went wrong.",
                        },
                        bubbles: true,
                    }),
                );
            }
        }

        /* ── Helpers ── */

        _resetSelections() {
            for (const card of this.querySelectorAll(
                "[data-bundle-card].is-selected",
            )) {
                card.classList.remove("is-selected");
            }
            for (const summary of this.querySelectorAll(
                "[data-bundle-summary]",
            )) {
                summary.hidden = true;
                this._clearError(summary);
            }
            this._dispatchSelectionChange(null);
        }

        _showError(summary, message) {
            if (!summary) return;
            let errorEl = summary.querySelector("[data-bundle-error]");
            if (!errorEl) {
                errorEl = document.createElement("div");
                errorEl.setAttribute("data-bundle-error", "");
                errorEl.id = `bundle-error-${Date.now()}`;
                errorEl.className = "bundle-summary__error";
                errorEl.setAttribute("role", "alert");
                summary.appendChild(errorEl);
            }
            errorEl.textContent = message;

            const confirmBtn = summary.querySelector("[data-bundle-confirm]");
            if (confirmBtn)
                confirmBtn.setAttribute("aria-describedby", errorEl.id);
        }

        _clearError(summary) {
            if (!summary) return;
            const errorEl = summary.querySelector("[data-bundle-error]");
            if (errorEl) {
                const confirmBtn = summary.querySelector(
                    "[data-bundle-confirm]",
                );
                if (confirmBtn) confirmBtn.removeAttribute("aria-describedby");
                errorEl.remove();
            }
        }

        _refreshTrapFocus() {
            if (this._isHostOpen()) {
                if (typeof removeTrapFocus === "function") removeTrapFocus();
                if (typeof trapFocus === "function") trapFocus(this._host);
            }
        }

        _focusFirstInteractive(container) {
            requestAnimationFrame(() => {
                const target = container.querySelector(
                    'a[href], button:not([disabled]), select, [tabindex]:not([tabindex="-1"])',
                );
                target?.focus({ preventScroll: true });
            });
        }

        _formatMoney(cents) {
            const fmt = window.theme?.settings?.moneyFormat;
            if (window.Shopify?.formatMoney && fmt) {
                return window.Shopify.formatMoney(cents, fmt);
            }
            return "$" + (cents / 100).toFixed(2);
        }
    }

    customElements.define("bundle-save-widget", BundleSaveWidget);
}
